import { getVerificationLinkV2 } from "../api/bseApi";

export function buildGetKycLinkPayload(clientCode, memberId, event = "ucc_kyc_pending") {
  const code = String(clientCode || "").trim();
  const mid = String(memberId || process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim();
  return {
    data: [
      {
        event,
        investor: { client_code: code },
        member_code: mid,
      },
    ],
  };
}

export const buildGet2faLinkPayload = buildGetKycLinkPayload;

export function parseBseApiMessages(body) {
  if (!body) return "";
  const messages = body?.messages || body?.data?.messages;
  if (Array.isArray(messages) && messages.length) {
    return messages
      .map((m) => {
        if (typeof m === "string") return m;
        const code = m?.errcode || "";
        const field = m?.field ? ` (${m.field})` : "";
        const vals = Array.isArray(m?.vals) ? m.vals.filter(Boolean).join(", ") : "";
        const text = m?.msg_text || m?.message || m?.msg || "";
        if (text) return text;
        if (code === "operation_failed") {
          return vals
            ? `BSE operation_failed for ${vals} — UCC may still need member-portal approval`
            : "BSE operation_failed — UCC may still need member-portal approval";
        }
        return vals ? `${code}${field}: ${vals}` : `${code}${field}` || JSON.stringify(m);
      })
      .filter(Boolean)
      .join(" ");
  }
  return body?.message || "";
}

export function isBseApiSuccess(body) {
  if (!body || typeof body !== "object") return false;
  if (String(body.status || "").toLowerCase() === "error") return false;
  const messages = body.messages || body?.data?.messages;
  if (Array.isArray(messages) && messages.length) return false;
  return true;
}

function pickUrlFromObject(obj, depth = 0) {
  if (!obj || typeof obj !== "object" || depth > 6) return null;
  const urlKeys = [
    "verification_url",
    "link",
    "url",
    "kyc_link",
    "auth_link",
    "verification_link",
    "redirect_url",
    "return_url",
  ];
  for (const key of urlKeys) {
    const v = obj[key];
    if (typeof v === "string" && /^https?:\/\//i.test(v.trim())) {
      return v.trim();
    }
  }
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = pickUrlFromObject(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  for (const value of Object.values(obj)) {
    const found = pickUrlFromObject(value, depth + 1);
    if (found) return found;
  }
  return null;
}

export function extractKycLinkFromResponse(body) {
  return pickUrlFromObject(body);
}

/**
 * Server picks KYC vs 2FA; handles PENDING_VERIFICATION (member approval).
 */
export async function fetchInvestorVerificationLink(clientCode, memberId) {
  const code = String(clientCode || "").trim();
  const mid = String(memberId || process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim();

  if (!code) {
    throw new Error("Client code is required");
  }

  try {
    const res = await getVerificationLinkV2(buildGetKycLinkPayload(code, mid));
    const body = res?.data;

    if (!isBseApiSuccess(body)) {
      const err = new Error(parseBseApiMessages(body) || "Verification link not available");
      err.response = { data: body };
      throw err;
    }

    if (body.action_required === "member_portal_approval") {
      return {
        memberApproval: true,
        body,
        uccStatus: body.ucc_status || "",
        message: body.message || "",
        nextSteps: Array.isArray(body.next_steps) ? body.next_steps : [],
        portalUrl: body.portal_url || "https://starmfv2demo.bseindia.com",
        eventsTriedKyc: body.events_tried_kyc || [],
        eventsTried2fa: body.events_tried_2fa || body.events_tried || [],
      };
    }

    const link = extractKycLinkFromResponse(body);
    if (!link) {
      const err = new Error("BSE did not return a verification URL");
      err.response = { data: body };
      throw err;
    }

    const linkType = String(body.link_type || "kyc").toLowerCase();
    return {
      link,
      body,
      kind: linkType === "2fa" ? "2fa" : "kyc",
      eventUsed: body.event_used || "",
      kycType: body.kyc_type || "",
      note: body.note || "",
      uccStatus: body.ucc_status || "",
    };
  } catch (err) {
    if (err.response?.data) throw err;
    const err2 = new Error(err?.message || "Network error");
    err2.response = err.response;
    throw err2;
  }
}

export function formatVerificationLinkError(err) {
  const api = err?.response?.data;
  const data = api?.data || {};
  const parts = [];

  parts.push(parseBseApiMessages(api) || err?.message || "Request failed");

  const uccSt = String(data.ucc_status || api?.ucc_status || "").toUpperCase();
  const raw = JSON.stringify(api || {});
  const isOperationFailed =
    /operation_failed/i.test(raw) || /operation_failed/i.test(parts[0] || "");

  if (uccSt.includes("PENDING_VERIFICATION") || isOperationFailed) {
    parts.push(
      "<strong>Next step:</strong> Approve this UCC in the <strong>BSE StAR MF member portal</strong>, wait a few minutes, then click KYC link again. Investor KYC/2FA links are usually not issued until member approval."
    );
    const portal = data.portal_url || api?.portal_url || "https://starmfv2demo.bseindia.com";
    parts.push(`<a href="${portal}" target="_blank" rel="noopener noreferrer">Open BSE portal</a>`);
  } else if (data.kyc_type === "K" || String(data.kyc_type).toUpperCase() === "K") {
    parts.push(
      "<strong>KRA-verified (kyc_type K):</strong> Video KYC does not apply. The server tried 2FA events (ucc_elog, ucc_auth, nominee, PAN, etc.)."
    );
  }

  if (data.hint) parts.push(data.hint);
  if (data.diagnostic) parts.push(data.diagnostic);
  if (data.ucc_status) parts.push(`UCC status: ${data.ucc_status}`);

  if (!data.hint && !data.diagnostic && !uccSt.includes("PENDING_VERIFICATION") && !isOperationFailed) {
    parts.push("If you just completed registration, wait 5–30 minutes and try again.");
  }

  return parts.filter(Boolean).join("<br/><br/>");
}

export function openVerificationLinkSuccess(result) {
  if (result.memberApproval) {
    const steps = (result.nextSteps || [])
      .map((s) => `<li>${s}</li>`)
      .join("");
    const portal = result.portalUrl || "https://starmfv2demo.bseindia.com";
    return {
      title: "Approve UCC in BSE portal",
      html: `
        <p>${result.message || "BSE has not issued an investor KYC/2FA URL for this UCC yet."}</p>
        <p><strong>UCC status:</strong> ${result.uccStatus || "PENDING_VERIFICATION"}</p>
        <p class="small text-muted mb-2">
          We called BSE <code>get_kyc_link</code> / <code>get_2fa_link</code> (SDK flow). No URL was returned for this status.
        </p>
        <p>
          As GPSF member admin, open the BSE portal and <strong>approve this UCC</strong>, then click KYC link again.
        </p>
        <p><a href="${portal}" target="_blank" rel="noopener noreferrer">Open BSE StAR MF portal</a></p>
        ${steps ? `<ul class="text-start small mb-0">${steps}</ul>` : ""}
      `,
      isInfo: true,
      portalUrl: portal,
    };
  }

  const { link, kind, eventUsed, kycType, note, uccStatus } = result;
  const label = kind === "2fa" ? "2FA verification" : "KYC verification";
  window.open(link, "_blank", "noopener,noreferrer");
  const kraNote =
    kycType === "K" || kind === "2fa"
      ? "<br/><span class=\"text-muted small\">KRA-verified profile — complete steps in the opened page.</span>"
      : "";
  return {
    title: `${label} link opened`,
    html: `${note ? `${note}<br/>` : ""}${uccStatus ? `Status: <strong>${uccStatus}</strong><br/>` : ""}Complete steps in the new tab.<br/>Event: <strong>${eventUsed || "—"}</strong>${kraNote}<br/><br/><a href="${link}" target="_blank" rel="noopener noreferrer">Open link again</a>`,
    isInfo: false,
  };
}
